/**
 * Player-facing text, keyed, so the game can one day speak more than English.
 *
 * **T124 · D7 · `Development/08_Language_And_Translation.md`.**
 *
 * ⚠️ **This adds no translation. The game is entirely in English, exactly as it
 * was.** What it adds is the one thing that is expensive to retrofit: every
 * sentence the player reads now lives in a file with a name, instead of being a
 * literal in the middle of the code.
 *
 * ## The three rules, and why each one is here
 *
 * **1. Whole sentences per key. Never join fragments at runtime.**
 * Icelandic nouns inflect — *birki*, *birkið*, *úr birki* — so
 * `'You picked up ' + name` cannot be made correct in Icelandic by any
 * translation, however good. It has to be rewritten. A key whose value is
 * `"Holding {block}."` can be rewritten *per language* in the file, because
 * each language chooses its own sentence shape; a `+` in the code cannot.
 *
 * **2. Plurals come from `Intl`, never from `count === 1`.**
 * 🔴 In Icelandic **21 takes the singular** — *21 fiskur*, not *21 fiskar* —
 * and so do 31 and 101, while 11 does not. `count === 1 ? a : b` is therefore
 * wrong in Icelandic, and wrong in a way nobody testing with 1, 2 and 5 would
 * ever see: **it breaks first at 21.** `Intl.PluralRules` carries the real
 * Unicode rules for every language, in every browser and in Node, for free.
 *
 * **3. Sorted lists go through {@link compareText}.**
 * þ, æ and ö sort **after** z in Icelandic. A list sorted with `<` looks right
 * to everybody except the people this game is for.
 *
 * ## Why our own sixty lines and not a library
 *
 * `@lofi/shared` is deliberately zero-dependency, and **D2** means haugur has to
 * be able to read what is here. `Intl` does the genuinely hard parts. ⚠️ **The
 * file format is deliberately something a real library could read** — flat keys,
 * `{name}` placeholders, one JSON object — so moving to FormatJS or i18next
 * later is a change of loader and not a re-translation.
 *
 * ## Adding a language
 *
 * Drop `lang/<code>.json` beside `en.json` and add one line to {@link RAW_TABLES}.
 * ⚠️ **Two lines, deliberately, rather than discovering files at runtime**: a
 * zero-dependency package that runs both in Node and inside a browser bundle
 * cannot list a directory the same way in both, and that machinery is more than
 * the line it would save (newSession §7 rule 7).
 * `08_Language_And_Translation.md` proposed discovery; this is the smaller
 * version of the same thing.
 */

import enMessages from '../lang/en.json' with { type: 'json' };

/**
 * The plural categories Unicode defines.
 *
 * English and Icelandic use only `one` and `other`; Polish uses four and Arabic
 * six, which is why this is not a boolean.
 */
export type PluralCategory = 'zero' | 'one' | 'two' | 'few' | 'many' | 'other';

/** One entry in a language file: a sentence, or one sentence per plural category. */
export type Message = string | Readonly<Partial<Record<PluralCategory, string>>>;

/** A whole language file, as it is written on disk. */
export type MessageTable = Readonly<Record<string, Message>>;

/**
 * Every key the game has.
 *
 * 🔴 **This is what makes a mistyped key a compile error rather than a word
 * missing from somebody's screen**, and it is the reason {@link t} takes this
 * type instead of a plain string.
 */
export type MessageKey = keyof typeof enMessages;

/** The language the game falls back to, and the one translators copy. */
export const DEFAULT_LANGUAGE = 'en';

/**
 * The language files that exist. **Add a line here when you add a file.**
 *
 * ⚠️ Kept in its JSON shape as well as the compiled one below, because the test
 * that keeps the files in step compares their *keys*, and that is easier to read
 * off the original object.
 */
const RAW_TABLES: ReadonlyMap<string, MessageTable> = new Map<string, MessageTable>([
  [DEFAULT_LANGUAGE, enMessages],
]);

/**
 * The same tables, turned into `Map`s **once at startup**.
 *
 * ⚠️ **Once, not per call.** The HUD asks for about fifteen of these six times a
 * second; rebuilding a `Map` from the JSON each time would be allocation in a
 * render loop for no reason. `Map` rather than plain property access also keeps
 * `security/detect-object-injection` happy, which is the right rule here — the
 * key can arrive from the network (protocol 8).
 */
const COMPILED: ReadonlyMap<string, ReadonlyMap<string, Message>> = new Map(
  [...RAW_TABLES].map(([code, table]) => [code, new Map(Object.entries(table))]),
);

/** English, kept to hand because it is what every miss falls back to. */
const FALLBACK = COMPILED.get(DEFAULT_LANGUAGE) ?? new Map<string, Message>();

let currentCode: string = DEFAULT_LANGUAGE;

/** Which language the game is speaking. */
export function currentLanguage(): string {
  return currentCode;
}

/** The languages there are files for, English first. */
export function availableLanguages(): readonly string[] {
  return [...RAW_TABLES.keys()];
}

/**
 * Choose a language. Returns `false`, and changes nothing, if we have no file
 * for it.
 *
 * ⚠️ **Returning `false` rather than throwing is deliberate.** The caller is a
 * browser handing us `navigator.language`, which may be any of several hundred
 * codes; not having a translation is an ordinary answer, not an error.
 */
export function setLanguage(code: string): boolean {
  if (!COMPILED.has(code)) return false;
  currentCode = code;
  return true;
}

/** Cached because constructing one of these is not free, and the HUD is a loop. */
const pluralRules = new Map<string, Intl.PluralRules>();
const numberFormats = new Map<string, Intl.NumberFormat>();
const collators = new Map<string, Intl.Collator>();

function pluralRulesFor(code: string): Intl.PluralRules {
  const existing = pluralRules.get(code);
  if (existing) return existing;
  const made = new Intl.PluralRules(code);
  pluralRules.set(code, made);
  return made;
}

/** What may be dropped into a `{placeholder}`. */
export type MessageValues = Readonly<Record<string, string | number>>;

function fill(template: string, values: MessageValues | undefined): string {
  if (!values) return template;
  const supplied = new Map(Object.entries(values));
  /* An unknown placeholder is left standing rather than blanked, because
     `{blokk}` on screen says which key is wrong and an empty gap does not. */
  return template.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = supplied.get(name);
    if (value === undefined) return whole;
    return typeof value === 'number' ? formatNumber(value) : value;
  });
}

/**
 * The sentence for a key, with its placeholders filled in.
 *
 * A plural entry needs `count` among the values. ⚠️ **A key that does not exist
 * returns the key itself** — visible, searchable, and never a blank space where
 * a sentence should be.
 */
export function t(key: MessageKey, values?: MessageValues): string {
  return tAnyKey(key, values);
}

/**
 * The same, for a key that is **not** known at compile time — one that arrived
 * over the network, or was assembled from a number.
 *
 * 🔴 **Prefer {@link t}.** This exists because the server tells the client *why*
 * an edit was refused by sending a key (protocol 8), and because the world
 * calendar's month names are looked up by number. Everywhere else, a typed key
 * turns a typo into a failed build.
 */
export function tAnyKey(key: string, values?: MessageValues): string {
  const message = COMPILED.get(currentCode)?.get(key) ?? FALLBACK.get(key);
  if (message === undefined) return key;
  if (typeof message === 'string') return fill(message, values);

  const count = values?.count;
  if (typeof count !== 'number') return key;
  const form = selectPluralForm(message, count);
  return form === undefined ? key : fill(form, values);
}

/**
 * Pick the right sentence out of a plural entry, for a given count.
 *
 * 🔴 **This is the function the whole design turns on, so it is exported and
 * tested directly** rather than only through whatever key happens to use it
 * today. `Intl` decides the category: for Icelandic, **21 is `one`** and 11 is
 * `other`, which is the opposite of English and is why `count === 1` is wrong
 * here.
 *
 * ⚠️ Falls back to `other`, which every language has, so a file that is missing
 * a category shows a slightly wrong sentence rather than a bare key.
 */
export function selectPluralForm(
  forms: Readonly<Partial<Record<PluralCategory, string>>>,
  count: number,
  language: string = currentCode,
): string | undefined {
  /* ⚠️ No cast: TypeScript's own `Intl.LDMLPluralRule` is exactly the union
     {@link PluralCategory} declares, so the two agree by construction — and if a
     future TypeScript widens one of them, this line stops compiling, which is
     the right moment to find out. */
  const category: PluralCategory = pluralRulesFor(language).select(count);
  /* A small `Map` built here rather than at startup: only plural keys reach
     this, and they are rare — the hot path above allocates nothing. */
  const lookup = new Map(Object.entries(forms));
  return lookup.get(category) ?? lookup.get('other');
}

/**
 * A number as the current language writes it — `1,234.5` in English,
 * **`1.234,5`** in Icelandic.
 *
 * ⚠️ **For quantities, not for identifiers.** An ISN93 coordinate is a grid
 * reference, not an amount, and grouping separators in it would be wrong in
 * every language — so those call sites format the digits themselves and pass a
 * string.
 */
export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
  if (options) return new Intl.NumberFormat(currentCode, options).format(value);
  const existing = numberFormats.get(currentCode);
  if (existing) return existing.format(value);
  const made = new Intl.NumberFormat(currentCode);
  numberFormats.set(currentCode, made);
  return made.format(value);
}

/**
 * Compare two pieces of text for sorting, the way the current language sorts.
 *
 * 🔴 **Use this for anything the player sees in order** — an inventory, a list
 * of place names. In Icelandic **þ, æ and ö come after z**, and `a < b` puts
 * them in the middle.
 */
export function compareText(a: string, b: string): number {
  const existing = collators.get(currentCode);
  if (existing) return existing.compare(a, b);
  const made = new Intl.Collator(currentCode);
  collators.set(currentCode, made);
  return made.compare(a, b);
}

/**
 * Every language file, for the test that keeps them in step.
 *
 * ⚠️ Exported for `i18n.test.ts` and nothing else. The test asserting that every
 * language file carries exactly English's keys is the cheapest thing in this
 * module and the only thing that stops translations rotting.
 */
export function languageFiles(): ReadonlyMap<string, MessageTable> {
  return RAW_TABLES;
}
